import { useEffect, useState } from 'react'
import { THEMES, type Theme, applyTheme, storedTheme } from '../theme'

const LABEL: Record<Theme, string> = {
  system: 'Match the system setting',
  light: 'Light',
  dark: 'Dark',
}

/**
 * Light / dark / system, as a three-way segmented control.
 *
 * Not a two-state switch: a switch has to lie about which way it points when the
 * choice is "whatever the machine is doing", and it gives no way back to that once
 * you have touched it.
 *
 * The initial value is read from storage rather than assumed, because the pre-paint
 * script in `index.html` has already applied it — this only has to agree with what is
 * on screen, never to set it for the first time.
 */
export function ThemeToggle() {
  const [theme, setTheme] = useState<Theme>(storedTheme)

  function choose(next: Theme) {
    setTheme(next)
    applyTheme(next)
  }

  // With "system" chosen there is no attribute on the document, so the OS switching at
  // sunset repaints the page on its own and nothing here needs to run. The listener
  // exists only to keep the control's own icon honest.
  useEffect(() => {
    if (theme !== 'system') return
    const query = window.matchMedia('(prefers-color-scheme: dark)')
    const repaint = () => setTheme('system')
    query.addEventListener('change', repaint)
    return () => query.removeEventListener('change', repaint)
  }, [theme])

  return (
    <div className="theme-toggle" role="group" aria-label="Colour theme">
      {THEMES.map((option) => (
        <button
          key={option}
          type="button"
          className="theme-toggle__button"
          aria-pressed={theme === option}
          title={LABEL[option]}
          onClick={() => choose(option)}
        >
          <Icon theme={option} />
          <span className="visually-hidden">{LABEL[option]}</span>
        </button>
      ))}
    </div>
  )
}

/** Stroked 16px icons on the same grid, so the three read as one control. */
function Icon({ theme }: { theme: Theme }) {
  const common = {
    width: 15,
    height: 15,
    viewBox: '0 0 24 24',
    fill: 'none',
    stroke: 'currentColor',
    strokeWidth: 1.8,
    strokeLinecap: 'round' as const,
    strokeLinejoin: 'round' as const,
    'aria-hidden': true,
  }

  if (theme === 'light') {
    return (
      <svg {...common}>
        <circle cx="12" cy="12" r="4" />
        <path d="M12 3v2M12 19v2M3 12h2M19 12h2M5.6 5.6l1.4 1.4M17 17l1.4 1.4M18.4 5.6L17 7M7 17l-1.4 1.4" />
      </svg>
    )
  }

  if (theme === 'dark') {
    return (
      <svg {...common}>
        <path d="M20 14.5A8 8 0 1 1 9.5 4a6.5 6.5 0 0 0 10.5 10.5z" />
      </svg>
    )
  }

  // "System" is a display, not a half-sun/half-moon — the choice is about deferring to
  // the machine, and a split disc reads as a third colour scheme instead.
  return (
    <svg {...common}>
      <rect x="3" y="4" width="18" height="12" rx="1.5" />
      <path d="M9 20h6M12 16v4" />
    </svg>
  )
}
